/**
 * The missions list: one live row per mission, grouped in the order of attention (Needs you,
 * Failed, Running, Queued, then Finished today and Earlier, folded). A row is the identity of
 * its item (kind, key, title), at most two quiet facts (what it waits on, the project), the
 * compact stage dots, the agent, elapsed and cost in fixed right-aligned columns, and for a
 * mission that needs you an inline Answer. (The backlog table is requirements/TableView.)
 */
import { ChevronDown, CircleAlert, Pause, Play, Search, Target, X } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ITEM_KIND_LABEL } from '@orgus/protocol';
import type { Id, MachineLoadState, MissionState } from '@orgus/protocol';
import { AgentOrb, Badge, Button, IconButton, Input, Panel, Pressable, RelativeTime, StateGroup, Tooltip, cn, formatCount, formatElapsed, formatMoney, formatTokens, plural } from '@orgus/ui';
import type { AgentOrbState, WorkState } from '@orgus/ui';
import { plainText } from '../../lib/plainText.ts';
import { useExt } from '../../lib/store.ts';
import { useCan } from '../auth/useMe.ts';
import { AgentGlyph, agentHue } from '../adapters/AgentKind.tsx';
import { MACHINE_LOAD_STATE_EXT } from '../machine-load/model.ts';
import { findWaitingInfo } from '../machine-load/cpuWaiting.ts';
import { WaitingForCpuBadge } from '../machine-load/WaitingForCpuBadge.tsx';
import { KeyLabel } from '../requirements/parts.tsx';
import { setMissionPaused } from './data.ts';
import { isBlocked } from './glance.ts';
import { MissionAnswer, useAnswerRoute } from './MissionAnswer.tsx';
import { MissionStages } from './MissionStages.tsx';
import { finishedAt, spendLabel } from './model.ts';
import type { MissionRow } from './model.ts';
import { KindIcon } from './parts.tsx';
import { missionStages } from './stages.ts';
import type { MissionStage } from './stages.ts';

const DAY = 24 * 60 * 60_000;

export type MissionGroupId = 'waiting' | 'failed' | 'running' | 'queued' | 'today' | 'earlier';

/** Groups in the order of attention; finished ones fold. */
export const MISSION_GROUPS: readonly { id: MissionGroupId; label: string; state: WorkState; collapsed: boolean }[] = [
  { id: 'waiting', label: 'Needs you', state: 'needs-you', collapsed: false },
  // ADR 0103: folded with its count; repeats are one row each already.
  { id: 'failed', label: 'Failed', state: 'failed', collapsed: true },
  { id: 'running', label: 'Running', state: 'running', collapsed: false },
  { id: 'queued', label: 'Queued', state: 'queued', collapsed: false },
  { id: 'today', label: 'Finished today', state: 'landed', collapsed: true },
  { id: 'earlier', label: 'Finished earlier', state: 'stale', collapsed: true },
];

/** Which group a mission sits in: by its state, and for a finished one by the day it finished. */
export function missionGroupOf(r: MissionRow, now: number): MissionGroupId {
  const s: MissionState = r.view.state;
  if (s === 'waiting') return 'waiting';
  if (s === 'failed') return 'failed';
  if (s === 'running') return 'running';
  if (s === 'planned') return 'queued';
  return Math.floor(finishedAt(r.mission) / DAY) === Math.floor(now / DAY) ? 'today' : 'earlier';
}

/** How long it has run: from its first start to now, or to when it finished. Nothing before it starts. */
export function missionElapsed(r: MissionRow, now: number): number | null {
  const first = r.mission.launches[0]?.at;
  // A start that never became a session ran for no time at all: an empty cell, not "0s".
  if (first == null || !r.view.sessions.some((s) => s.session)) return null;
  const end = r.view.state === 'done' || r.view.state === 'stopped' || r.view.state === 'failed' ? finishedAt(r.mission) : now;
  return Math.max(0, end - first);
}

const ORB: Record<MissionGroupId, AgentOrbState> = { waiting: 'needs-you', failed: 'failed', running: 'working', queued: 'idle', today: 'finished', earlier: 'ended' };

function matches(r: MissionRow, q: string): boolean {
  const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const text = `${r.mission.title} ${r.mission.goal} ${r.item?.title ?? ''} ${r.item?.key ?? ''} ${r.mission.id}`.toLowerCase();
  return terms.every((t) => text.includes(t));
}

/** Rows a group shows before "Show more" (ADR 0103): a long group never renders all at once. */
export const GROUP_PAGE = 50;

/** How many missions a row stands for: a run of ended repeats is one row (foldEndedMissions). */
export const rowCount = (r: MissionRow): number => r.mission.repeats?.count ?? 1;

const COL = { stages: 'w-16', elapsed: 'w-16', spend: 'w-20', trail: 'w-24', action: 'w-24' } as const;

/**
 * A finished mission's stage dots rest: nothing on it is under way or waiting any more, so a stage
 * left current or waiting on a person reads as not reached (failed and done keep their tone).
 */
export function restingStages(stages: readonly MissionStage[]): MissionStage[] {
  return stages.map((s) => (s.state === 'current' || s.state === 'attention' ? { ...s, state: 'waiting' } : s));
}
const num = 'shrink-0 truncate text-right text-ui text-fg-muted num';

/** Pause or resume one mission from its row (ADR 0103): shown on hover, always while paused. */
function PauseRowAction({ id, title, paused }: { id: Id; title: string; paused: boolean }) {
  const can = useCan('operator');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  if (!can) return null;
  const run = async () => {
    setBusy(true);
    setFailed(false);
    try {
      await setMissionPaused(id, !paused);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className={cn('pointer-events-auto hidden shrink-0 sm:flex', !paused && !failed && 'opacity-0 transition-opacity duration-(--duration-fast) group-hover/mrow:opacity-100 focus-within:opacity-100')}>
      <IconButton
        size="xs"
        icon={paused ? <Play /> : <Pause />}
        label={failed ? `${paused ? 'Resume' : 'Pause'} failed: the hub did not answer. Try again` : `${paused ? 'Resume' : 'Pause'} mission ${title}`}
        disabled={busy}
        onClick={() => void run()}
        className={failed ? 'text-status-danger' : undefined}
      />
    </span>
  );
}

function MissionLine({
  r,
  group,
  now,
  projectName,
  showProject,
  priced,
  onOpen,
}: {
  r: MissionRow;
  group: MissionGroupId;
  now: number;
  projectName: (id: Id) => string;
  showProject: boolean;
  priced: boolean;
  onOpen: (id: Id) => void;
}) {
  const v = r.view;
  const blocked = isBlocked(r);
  const loadSlice = useExt<MachineLoadState>(MACHINE_LOAD_STATE_EXT);
  const cpuInfo = findWaitingInfo(loadSlice, { sessionId: v.sessions.find((s) => s.session)?.session?.id, what: r.mission.title });
  // How its question is answered here: a held request, a message, Approve and resume. None: the mission page.
  const route = useAnswerRoute(group === 'waiting' ? r : null);
  const [answering, setAnswering] = useState(false);
  const finished = group === 'today' || group === 'earlier';
  const ended = finished || group === 'failed';
  const paused = !ended && !!r.mission.paused;
  const label = paused ? 'Paused' : blocked ? 'Blocked' : MISSION_GROUPS.find((g) => g.id === group)!.label;
  const repeats = r.mission.repeats && r.mission.repeats.count > 1 ? r.mission.repeats : null;
  // What it waits on, or why it failed: the fact that matters most, first.
  const why = v.blockers[0] ? plainText(v.blockers[0].title) : '';
  const title = r.item?.title ?? r.mission.title;
  const agent = v.sessions.find((s) => s.session)?.session?.agentKind ?? null;
  const elapsed = missionElapsed(r, now);
  const over = v.budgetUsed != null && v.budgetUsed >= 1;
  const tokens = v.spend.tokens.input + v.spend.tokens.output;
  const live = missionStages(r.mission, v);
  const stages = finished ? restingStages(live) : live;
  const open = () => onOpen(r.mission.id);
  const updated = <RelativeTime ts={finished ? finishedAt(r.mission) : r.mission.updatedAt} />;
  // One Answer per row; a phone shows it on the caption line, a wider screen in its slot before the number columns (only one is ever displayed).
  const answer =
    group === 'waiting'
      ? (cls?: string) => (
          <Button
            size="sm"
            variant="secondary"
            className={cn('justify-between', cls)}
            aria-expanded={route ? answering : undefined}
            aria-controls={route ? `answer-${r.mission.id}` : undefined}
            iconRight={route ? <ChevronDown className={cn('transition-transform duration-(--duration-fast)', answering && 'rotate-180')} /> : undefined}
            onClick={() => (route ? setAnswering((a) => !a) : open())}
            aria-label={`${blocked ? 'Unblock' : 'Answer'} ${r.mission.title}`}
          >
            {blocked ? 'Unblock' : 'Answer'}
          </Button>
        )
      : null;
  const orb = <AgentOrb state={paused ? 'idle' : blocked ? 'failed' : ORB[group]} size={20} hue={agentHue(agent)} icon={<AgentGlyph kind={agent} />} label={label} />;
  return (
    <li className="group/mrow motion-arrive relative">
      {/*
        The row is a stretched door under its content (no button inside a button), so the Answer
        button sits in the row's flow: in its own slot before the number columns, which then line
        up on every row and end with Updated at the panel's edge. On a phone the row is two lines:
        the title (wrapping, never cut) over the updated time.
      */}
      <Pressable
        data-row=""
        data-mission-row={r.mission.id}
        onClick={open}
        aria-label={`Open mission ${r.mission.title}, ${label}`}
        className="absolute inset-0 rounded-control transition-colors duration-(--duration-fast) hover:bg-hover"
      />
      <div className="pointer-events-none relative flex min-h-row-2line items-center gap-3 px-row-x py-2 sm:h-row sm:min-h-0 sm:py-0">
        <span className="flex w-5 shrink-0 justify-center" title={label}>
          {/* Resting orbs (queued, finished) keep the same round shell as the live ones, in a neutral ring. */}
          {ORB[group] === 'idle' || finished || paused ? <span className="inline-flex rounded-pill ring-1 ring-border-strong">{orb}</span> : orb}
        </span>
        <span className="flex min-w-0 flex-1 flex-col sm:flex-row sm:items-center sm:gap-3 sm:overflow-hidden">
          <span className="hidden shrink-0 items-center gap-2 sm:flex">
            {r.item ? <KindIcon kind={r.item.kind} tooltip={false} /> : <Target className="size-4 text-fg-subtle" aria-label="Mission" />}
            {/* The key column keeps its width without a key, so every title starts at the same place. */}
            <span className="hidden w-16 sm:inline-flex">{r.item && <KeyLabel value={r.item.key} />}</span>
          </span>
          <span className={cn('line-clamp-2 min-w-0 text-ui font-medium wrap-anywhere sm:line-clamp-none sm:shrink sm:truncate', finished ? 'text-fg-muted' : 'text-fg')} title={r.item ? `${ITEM_KIND_LABEL[r.item.kind]}: ${title}` : title}>
            {title}
          </span>
          {/* Phone: the updated time on the caption line, and the Answer button at its end, so the title keeps the row's width. */}
          <span className="flex min-h-control-sm items-center gap-2 text-caption text-fg-subtle num sm:hidden" data-meta="">
            {updated}
            {answer && <span className="pointer-events-auto ml-auto">{answer(COL.action)}</span>}
          </span>
          {/* The one chip: paused, or how many times this ended the same way (ADR 0103). */}
          {paused ? (
            <Badge size="sm" icon={<Pause />} className="hidden shrink-0 sm:inline-flex">
              Paused
            </Badge>
          ) : repeats ? (
            <Tooltip content={<>Ended the same way {formatCount(repeats.count)} times since <RelativeTime ts={repeats.firstAt} /></>}>
              <Badge size="sm" className="pointer-events-auto hidden shrink-0 num sm:inline-flex">{formatCount(repeats.count)} times</Badge>
            </Tooltip>
          ) : null}
          {cpuInfo?.isWaiting ? (
            <span className="hidden min-w-16 flex-1 truncate text-caption sm:block">
              <WaitingForCpuBadge placeInLine={cpuInfo.placeInLine} estimateMinutes={cpuInfo.estimateMinutes} work={cpuInfo.work} />
            </span>
          ) : why ? (
            <span data-meta="" className={cn('hidden min-w-16 flex-1 truncate text-caption sm:block', group === 'waiting' || group === 'failed' ? 'text-fg-muted' : 'text-fg-subtle')} title={why}>
              {why}
            </span>
          ) : null}
          {showProject && (
            <span data-meta="" className="hidden shrink-0 truncate text-caption text-fg-subtle lg:block">
              {projectName(r.mission.projectId)}
            </span>
          )}
        </span>
        {answer && <span className={cn(COL.action, 'pointer-events-auto hidden shrink-0 items-center sm:flex')}>{answer('w-full')}</span>}
        {!ended && <PauseRowAction id={r.mission.id} title={r.mission.title} paused={paused} />}
        <span className={cn(COL.stages, 'hidden shrink-0 justify-end md:flex')} data-column="stages">
          <MissionStages stages={stages} variant="compact" />
        </span>
        <span className={cn(COL.elapsed, num, 'hidden text-caption md:block')} data-column="elapsed">
          {elapsed != null ? formatElapsed(elapsed) : ''}
        </span>
        {/* The amount in the quiet number colour; over budget a small danger mark says so, with the budget as its tooltip. Tokens on a model with no price show as tokens, never as $0.00. */}
        <span className={cn(COL.spend, num, 'hidden items-center justify-end gap-1 md:flex')} data-column="spend" title={priced && v.spend.cost != null && !over ? spendLabel(v.spend.cost, r.mission.budget) : undefined}>
          {over && priced && v.spend.cost != null && (
            <Tooltip content={spendLabel(v.spend.cost, r.mission.budget)}>
              <CircleAlert className="pointer-events-auto size-3 shrink-0 text-status-danger" aria-label="Over budget" />
            </Tooltip>
          )}
          <span className="truncate">{priced && v.spend.cost ? formatMoney(v.spend.cost) : tokens > 0 ? formatTokens(tokens) : ''}</span>
        </span>
        <span className={cn(COL.trail, 'hidden shrink-0 justify-end text-caption text-fg-subtle num sm:flex')} data-column="updated">
          {updated}
        </span>
      </div>
      {/* The answer opens under its row, in place: the same answer as the drawer and the mission page. */}
      {answering && route && (
        <div id={`answer-${r.mission.id}`} className="mb-2 ml-12 mr-row-x flex flex-col gap-3 rounded-card border border-border-subtle bg-bg p-3">
          {/* A held request's card names it already; a message or a resume says what it answers. */}
          {why && route.kind !== 'held' && <p className="text-ui text-fg">{why}</p>}
          <MissionAnswer row={r} route={route} />
        </div>
      )}
    </li>
  );
}

export function MissionList({
  rows,
  now,
  projectName,
  showProject,
  onOpen,
  empty,
  leading,
  trailing,
  showFinished,
}: {
  rows: MissionRow[];
  now: number;
  projectName: (id: Id) => string;
  showProject: boolean;
  onOpen: (id: Id) => void;
  empty: ReactNode;
  /** Controls before the search in the view bar: the page's view switch. */
  leading?: ReactNode;
  /** Controls at the end of the view bar: the project. */
  trailing?: ReactNode;
  /** Open the finished groups (a link to finished missions). */
  showFinished?: boolean;
}) {
  const [q, setQ] = useState('');
  /** Rows shown per group beyond the first page. */
  const [more, setMore] = useState<Partial<Record<MissionGroupId, number>>>({});
  const shown = useMemo(() => rows.filter((r) => matches(r, q)), [rows, q]);
  // Spend is money. Without cost tracking there are no dollars to show, so the column holds tokens.
  const priced = rows.some((r) => r.view.spend.cost != null);
  const groups = MISSION_GROUPS.map((g) => ({ ...g, rows: shown.filter((r) => missionGroupOf(r, now) === g.id) })).filter((g) => g.rows.length);
  // Finished groups fold under live work; with nothing live, today's finished ones are the news.
  const live = groups.some((g) => !g.collapsed);
  const folded = (g: (typeof groups)[number]) => (q || showFinished ? false : g.collapsed && (live || g.id !== 'today'));
  return (
    <div className="flex flex-col gap-3">
      <div className="flex min-h-filter-bar flex-wrap items-center gap-2" role="toolbar" aria-label="Missions views">
        {leading}
        <Input
          size="sm"
          icon={<Search />}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search missions"
          aria-label="Search missions"
          trailing={q ? <IconButton label="Clear search" icon={<X />} size="xs" onClick={() => setQ('')} /> : undefined}
          wrapperClassName="w-full sm:w-56"
        />
        {q && <span className="text-caption text-fg-subtle num">{plural(shown.reduce((n, r) => n + rowCount(r), 0), 'mission')}</span>}
        <div className="ml-auto flex items-center gap-2">{trailing}</div>
      </div>
      <Panel flush>
        {groups.length === 0 ? (
          empty
        ) : (
          <div className="px-2 pb-2" data-list="">
            <div className="hidden h-8 items-center gap-3 px-row-x md:flex" aria-hidden>
              <span className="w-5 shrink-0" />
              <span className="flex-1" />
              <span className={cn(COL.stages, 'shrink-0 text-right text-caption text-fg-subtle')}>Stages</span>
              <span className={cn(COL.elapsed, 'shrink-0 text-right text-caption text-fg-subtle')}>Elapsed</span>
              <span className={cn(COL.spend, 'shrink-0 text-right text-caption text-fg-subtle')}>{priced ? 'Cost' : 'Tokens'}</span>
              <span className={cn(COL.trail, 'shrink-0 text-right text-caption text-fg-subtle')}>Updated</span>
            </div>
            <div className="flex flex-col gap-2">
              {groups.map((g) => {
                const limit = GROUP_PAGE + (more[g.id] ?? 0);
                const rest = g.rows.length - limit;
                return (
                  <StateGroup key={`${g.id}:${folded(g) ? 'folded' : 'open'}`} groupId={`missions-${g.id}`} state={g.state} label={g.label} count={g.rows.reduce((n, r) => n + rowCount(r), 0)} defaultCollapsed={folded(g)}>
                    <ul className="flex flex-col">
                      {g.rows.slice(0, limit).map((r) => (
                        <MissionLine key={r.mission.id} r={r} group={g.id} now={now} projectName={projectName} showProject={showProject} priced={priced} onOpen={onOpen} />
                      ))}
                    </ul>
                    {rest > 0 && (
                      <div className="flex h-row items-center px-row-x">
                        <Button size="sm" variant="ghost" onClick={() => setMore((m) => ({ ...m, [g.id]: (m[g.id] ?? 0) + GROUP_PAGE }))}>
                          Show {formatCount(Math.min(rest, GROUP_PAGE))} more of {formatCount(rest)}
                        </Button>
                      </div>
                    )}
                  </StateGroup>
                );
              })}
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
}
